<template>
  <div class="assistant-editor" @dblclick="handleDblClick">
    <div class="assistant-editor__actions">
      <Button
          type="button"
          text
          severity="secondary"
          icon="pi pi-chevron-left"
          label="Каталог"
          @click="$emit('toCatalog')"
      />
      <div class="assistant-editor__actions-right">
        <Button v-if="modalState === ASSISTANT_MODAL_STATES.PREVIEW && props.assistant.can_edit"
                type="button"
                severity="secondary"
                size="small"
                icon="pi pi-trash"
                @click="$emit('delete', props.assistant.id)"
        />
        <Button v-if="modalState === ASSISTANT_MODAL_STATES.EDITING || modalState === ASSISTANT_MODAL_STATES.CREATING"
            type="button"
            size="small"
            icon="pi pi-save"
            :label="modalState === ASSISTANT_MODAL_STATES.EDITING ? 'Сохранить' : 'Создать'"
            @click="handleSave"
        />
        <Button v-if="modalState === ASSISTANT_MODAL_STATES.PREVIEW && props.assistant.can_edit"
            type="button"
            size="small"
            icon="pi pi-pencil"
            label="Редактировать"
            @click="$emit('edit', assistant)"
        />
      </div>
    </div>

    <div class="assistant-editor__body assistant-modal__body">
      <div class="assistant-editor__row">
        <div>
          <label for="name">Имя</label>
          <p class="text-muted">Будет отображаться в интерфейсе</p>
        </div>
        <InputText
            id="name"
            v-model="formData.name"
            class="assistant-editor__input"
            placeholder="Имя ассистента"
            :disabled="modalState === ASSISTANT_MODAL_STATES.PREVIEW"
        />
      </div>
      <div class="assistant-editor__row">
        <div>
          <label for="system_prompt">Инструкция</label>
          <p class="text-muted">Используется как системный промпт</p>
        </div>
        <Textarea
            id="system_prompt"
            v-model="formData.system_prompt"
            rows="5"
            class="assistant-editor__input"
            placeholder="Инструкция ассистенту"
            :disabled="modalState === ASSISTANT_MODAL_STATES.PREVIEW"
        />
      </div>
      <div class="assistant-editor__row">
        <div>
          <label for="temperature">Температура ({{ formData.temperature }})</label>
          <p class="text-muted">Креативность ответов.</p>
        </div>
        <div class="assistant-editor__input ">
          <Slider class="assistant-editor__slider"
                  id="temperature"
                  v-model="formData.temperature"
                  :min="0.1"
                  :max="1"
                  :step="0.1"
                  :disabled="modalState === ASSISTANT_MODAL_STATES.PREVIEW"
          />
          <p class="text-muted">
            Высокая температура приводит к более творческим и неожиданным ответам. Низкая температура - более точные ответы. Рекомендуемая температура 0.7
          </p>
        </div>
      </div>
      <div class="assistant-editor__row">
        <label for="icon">Иконка</label>
        <div  class="assistant-editor__icon-wrapper assistant-editor__input">
          <div class="assistant-editor__icon">
            <img
                v-if="preview"
                :src="preview"
            />
            <div v-if="!(modalState === ASSISTANT_MODAL_STATES.PREVIEW)"
                 class="chat-widget__database-file-upload"
                 @drop.prevent="handleDrop"
                 @dragover.prevent
                 @dragenter.prevent
                 @click="triggerFileInput"
            >
              <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileSelect"
                  accept=".png,.jpg,.jpeg"
                  class="chat-widget__database-file-input-hidden"
              />
              <div class="chat-widget__database-file-upload-content">
                <i class="pi pi-upload"></i>
                <span v-if="selectedFile">{{ selectedFile.name }}</span>
                <span v-else>Перетащите изображение сюда или кликните для добавления своей иконки</span>
              </div>
            </div>
          </div>
          <div class="assistant-editor__icons" v-if="!(modalState === ASSISTANT_MODAL_STATES.PREVIEW)">
            <p>Или выберите из готовых</p>
            <div class="assistant-editor__icons-wrapper">
              <img
                  v-for="icon in assistantIcons"
                  :key="icon"
                  :src="icon"
                  @click="clickPreview(icon)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import Button from "primevue/button";
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Slider from 'primevue/slider';
import {ASSISTANT_MODAL_STATES, DEFAULT_ASSISTANT_ICON_PATH} from "@/constants";
import {useReferenceStore} from "@/modules/chat/stores/referenceStore";

const assistantIcons = computed(() => useReferenceStore().assistants_icons);

const props = defineProps({
  assistant: {
    type: Object,
    default: null
  },
  modalState: {
    type: String,
    default: ASSISTANT_MODAL_STATES.CREATING,
  }
});

const emit = defineEmits(['toCatalog', 'save', 'edit', 'delete']);

const formData = ref({
  name: props.assistant?.name || '',
  system_prompt: props.assistant?.system_prompt || '',
  temperature: props.assistant?.temperature || 0.7,
  icon: props.assistant?.icon || DEFAULT_ASSISTANT_ICON_PATH,
});

const preview = ref(formData.value.icon);

const fileInput = ref(null);
const selectedFile = ref(null);

const handleSave = async () => {
  const data = {...formData.value};
  if (selectedFile.value) {
    data.file = selectedFile.value;
    data.icon = null;
  }
  emit('save', data);
  selectedFile.value = null;
};

const convertFileToBase64 = (file) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    preview.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const clickPreview = (icon) => {
  preview.value = icon;
  formData.value.icon = icon;
  selectedFile.value = null;
};

const triggerFileInput = () => {
  fileInput.value.click();
};
const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
    convertFileToBase64(file);
  }
};

const handleDrop = (event) => {
  const file = event.dataTransfer.files[0];
  if (file) {
    selectedFile.value = file;
    convertFileToBase64(file);
  }
};

const handleDblClick = (event) => {
  // Проверяем, что мы в режиме предпросмотра и ассистент доступен для редактирования
  if (props.modalState === ASSISTANT_MODAL_STATES.PREVIEW && props.assistant?.can_edit) {
    emit('edit', props.assistant);
  }
};
</script>
